import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { colors } from '../../Shared/colors'
import { useNavigate } from 'react-router-dom';
import { ClientsDatatable } from './ClientsDatatable';
import {
  useLazyGetAllClientsQuery,
} from "../../features/clientSlice";
import toast, { Toaster } from 'react-hot-toast';
import { useGetSingleHierarchyQuery, useGetActiveStaffQuery } from '../../features/employeeSlice';
import { Autocomplete, TextField } from '@mui/material';
import { useGetFavouriteLeadsLatestQuery } from '../../features/leadsSlice';
import { Puff } from 'react-loader-spinner'
import { decryptData } from '../../services/crypto';
import { usePaginationFromURL } from '../../hooks/usePaginationFromURL';

export const ClientsFirst = () => {
 
  let staffId = null;
  let storedStaffId = localStorage.getItem("id");

  if (storedStaffId) {
    try {
      staffId = decryptData(storedStaffId);
    } catch (error) {
      // Silent fallback - session may be expired
      staffId = null;
    }
  }

  let staffName = null;
  let storedStaffName = localStorage.getItem("staffName");

  if (storedStaffName) {
    try {
      staffName = decryptData(storedStaffName);
    } catch (error) {
      // Silent fallback
      staffName = null;
    }
  }

  let role = null;
  let storedStaffRole = localStorage.getItem("role");

  if (storedStaffRole) {
    try {
      role = decryptData(storedStaffRole);
    } catch (error) {
      // Silent fallback
      role = null;
    }
  }

  // Use URL-based pagination hook (Note: DataGrid uses 0-indexed pages, but we'll convert)
  const { currentPage: urlPage, pageSize, setPage: setUrlPage, setPageSize } = usePaginationFromURL({
    defaultPage: 1,
    defaultPageSize: 50
  });
  // Convert to 0-indexed for DataGrid
  const currentPage = urlPage - 1;
  const setCurrentPage = (page) => {
    // Accept both 0-indexed (from DataGrid) and 1-indexed values
    const pageNum = typeof page === 'function' ? page(currentPage) : page;
    setUrlPage(pageNum >= 0 ? pageNum + 1 : 1);
  };
  const [ClientData, setclientData] = useState({
    fullName: "",
    phoneNumber: "",
    employeeID: { id: staffId || "", label: staffName || "" },
  });
  const [ClientDatafilter, setclientDatafilter] = useState({
    fullName: "",
    phoneNumber: "",
    employeeID: { id: staffId || "", label: staffName || "" },
  });
  const [hasSearched, setHasSearched] = useState(false);
  
  const { data: hierarchyStaff } = useGetSingleHierarchyQuery({ staffId });
  const { data: staff } = useGetActiveStaffQuery();
  const { data: latestfavouriteleads, isLoading } = useGetFavouriteLeadsLatestQuery({ staffId });

  const [trigger, { data: clients, isFetching: isLazyFetching }] = useLazyGetAllClientsQuery();


  const handleChange = (e) => {
    const { name, value } = e.target;
    setclientData({
      ...ClientData,
      [name]: value,
    });
  };

  const navigate = useNavigate();

  const handleClick = () => {
    navigate('/leads/favourite');
  };
  const staffOptions = useMemo(() => {
    let options = [];
    if (hierarchyStaff) {
      hierarchyStaff.forEach((staff) => {
        options.push({
          id: staff?._id,
          label: staff?.firstName + " " + staff?.lastName,
        });
      });
    }
    return options;
  }, [hierarchyStaff]);

  const allStaffOptions = useMemo(() => {
    let options = [];
    if (staff) {
      staff.forEach((staff) => {
        options.push({
          id: staff?._id,
          label: staff?.firstName + " " + staff?.lastName,
        });
      });
    }
    return options;
  }, [staff]);


  // Initial load and pagination changes
  // Handle pagination changes (only when user has already searched and not on initial page)
  useEffect(() => {
    // Skip if initial load hasn't happened yet
    if (!hasSearched) return;
    
    // Skip if we're on page 1 (page 0 in 0-indexed) - this is handled by search/initial load
    if (currentPage === 0) return;
    
    // Only trigger on pagination changes if user has searched
    if (ClientDatafilter.employeeID?.id || staffId) {
      const { fullName, phoneNumber, employeeID } = ClientDatafilter;
      trigger({
        employeeId: employeeID?.id || staffId,
        phoneNumber: phoneNumber || "",
        fullName: fullName || "",
        page: currentPage + 1,
        pageSize,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);
  
  // Initial load on mount
  useEffect(() => {
    if (staffId && !hasSearched) {
      trigger({
        employeeId: staffId,
        phoneNumber: "",
        fullName: "",
        page: 1,
        pageSize,
      }).then(() => {
        setHasSearched(true);
        setclientDatafilter({
          fullName: "",
          phoneNumber: "",
          employeeID: { id: staffId || "", label: staffName || "" },
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    const { phoneNumber, employeeID, fullName } = ClientData;
    
    // Update filter state first
    const newFilter = {
      fullName: fullName || "",
      phoneNumber: phoneNumber || "",
      employeeID: employeeID || { id: staffId || "", label: staffName || "" },
    };
    
    // Set states
    setclientDatafilter(newFilter);
    setHasSearched(true);
    setCurrentPage(0); // Reset to page 1 (0-indexed)
    
    // Trigger the search
    trigger({
      employeeId: employeeID?.id || staffId,
      phoneNumber: phoneNumber || "",
      fullName: fullName || "",
      page: 1,
      pageSize: pageSize,
    })
      .then((res) => {
        toast.success("Clients loaded successfully!");
      })
      .catch((err) => {
        toast.error(err?.data?.error?.message || "Error loading clients.");
      });
  };

  const handleClearFilters = () => {
    const defaultEmployeeID = { id: staffId || "", label: staffName || "" };
    setclientData({
      fullName: "",
      phoneNumber: "",
      employeeID: defaultEmployeeID,
    });
    setclientDatafilter({
      fullName: "",
      phoneNumber: "",
      employeeID: defaultEmployeeID,
    });
    setCurrentPage(0);
    setHasSearched(false); // Reset search flag so useEffect can handle pagination
    
    trigger({
      employeeId: staffId,
      phoneNumber: "",
      fullName: "",
      page: 1,
      pageSize: pageSize,
    })
      .then((res) => {
        toast.success("Filters cleared!");
      })
      .catch((err) => {
        toast.error(err?.data?.error?.message || "Error loading clients.");
      });
  };


  return (
    <>
      <Main>
        <Search>
          <Heading>
            <SearchIcon>🔍</SearchIcon>
            <HeadingText>Search Client</HeadingText>
          </Heading>
          <SearchSection1>
            <InputSection>
              <TextField
                fullWidth
                size="small"
                label="Client Name"
                name="fullName"
                id="fullName"
                placeholder="Enter client name"
                value={ClientData.fullName}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: colors.white,
                    borderRadius: '8px',
                  },
                }}
              />
            </InputSection>
            <InputSection>
              <TextField
                fullWidth
                size="small"
                label="Phone Number"
                name="phoneNumber"
                id="phoneNumber"
                placeholder="Enter phone number"
                value={ClientData.phoneNumber}
                onChange={handleChange}
                variant="outlined"
                type="tel"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: colors.white,
                    borderRadius: '8px',
                  },
                }}
              />
            </InputSection>
            {(role === "ceo" || role === "admin" || role === "account") && (
              <StaffInputFeild>
                <Autocomplete
                  id="staff-autocomplete"
                  size="small"
                  options={allStaffOptions}
                  getOptionLabel={(option) => option.label}
                  onChange={(e, value) => {
                    const newEmployeeID = value || { id: staffId || "", label: staffName || "" };
                    const currentFullName = ClientData.fullName || "";
                    const currentPhoneNumber = ClientData.phoneNumber || "";
                    
                    // Update both states
                    setclientData({ 
                      ...ClientData, 
                      employeeID: newEmployeeID 
                    });
                    
                    const newFilter = {
                      fullName: currentFullName,
                      phoneNumber: currentPhoneNumber,
                      employeeID: newEmployeeID,
                    };
                    setclientDatafilter(newFilter);
                    setHasSearched(true);
                    setCurrentPage(0);
                    
                    // Trigger search immediately when staff changes
                    trigger({
                      employeeId: newEmployeeID?.id || staffId,
                      phoneNumber: currentPhoneNumber,
                      fullName: currentFullName,
                      page: 1,
                      pageSize: pageSize,
                    })
                      .then(() => {
                        toast.success("Clients loaded successfully!");
                      })
                      .catch((err) => {
                        toast.error(err?.data?.error?.message || "Error loading clients.");
                      });
                  }}
                  value={ClientData.employeeID}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Staff"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: colors.white,
                          borderRadius: '8px',
                        },
                      }}
                    />
                  )}
                />
              </StaffInputFeild>
            )}
          </SearchSection1>
          <SearchSection2>
            <ClearButton onClick={handleClearFilters} disabled={isLazyFetching}>
              Clear Filters
            </ClearButton>
            <SearchButton onClick={handleSearch} disabled={isLazyFetching}>
              {isLazyFetching ? (
                <>
                  <Spinner /> Searching...
                </>
              ) : (
                <>
                  <SearchIconSmall>🔍</SearchIconSmall> Search
                </>
              )}
            </SearchButton>
          </SearchSection2>
        </Search>
        <Favourite>
          <FavouriteHeading>
            <StarIcon>⭐</StarIcon>
            <HeadingText>Favourite Clients</HeadingText>
          </FavouriteHeading>
          <FavouriteSection>
            {isLoading ? (
              <LoadingContainer>
                <Puff
                  height="60"
                  width="60"
                  color={colors.brown}
                  ariaLabel="puff-loading"
                  visible={true}
                />
                <LoadingText>Loading favourites...</LoadingText>
              </LoadingContainer>
            ) : (
              <>
                {latestfavouriteleads && latestfavouriteleads.length > 0 ? (
                  latestfavouriteleads.map((lead, index) => (
                    <FavouriteData key={index}>
                      <FavouriteDataLeft>
                        <ClientName>{lead?.clientName}</ClientName>
                        <LeadCountBadge>
                          {lead?.totalLeads} {lead?.totalLeads > 1 ? 'leads' : 'lead'}
                        </LeadCountBadge>
                      </FavouriteDataLeft>
                      <FavouriteDataRight>
                        <LeadId>ID: {lead?.leadID}</LeadId>
                        <DateText>
                          {new Date(lead.date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </DateText>
                      </FavouriteDataRight>
                    </FavouriteData>
                  ))
                ) : (
                  <EmptyFavouriteState>
                    <EmptyIcon>📋</EmptyIcon>
                    <EmptyText>No favourite clients found</EmptyText>
                    <EmptySubtext>Start favouriting clients to see them here</EmptySubtext>
                  </EmptyFavouriteState>
                )}
              </>
            )}
          </FavouriteSection>
          {!isLoading && latestfavouriteleads && latestfavouriteleads.length > 0 && (
            <ViewAllButton onClick={handleClick}>
              View All Favourites →
            </ViewAllButton>
          )}
        </Favourite>
      </Main>
      <Toaster />
      <ClientsDatatable
        clients={clients}
        isAutoFetching={isLazyFetching}
        currentPage={currentPage}
        totalPages={Math.ceil(clients?.total / pageSize)}
        pageSize={pageSize}
        setPageSize={setPageSize}
        setPage={setCurrentPage}
      />
    </>

  )
}

const Main = styled.div`
  width: 90%;
  max-width: 1400px;
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 2rem;
  min-height: 30vh;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    width: 95%;
  }
`

const Search = styled.div`
  background-color: ${colors.white};
  width: 65%;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  min-height: 19em;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 2rem;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
  }
  
  @media (max-width: 1024px) {
    width: 100%;
  }
`

const Heading = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${colors.text};
  font-size: 1.5rem;
  font-weight: 600;
  border-bottom: 2px solid ${colors.inputBorderWhite};
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
`

const HeadingText = styled.span`
  color: ${colors.text};
  font-size: 1.5rem;
  font-weight: 600;
`

const SearchIcon = styled.span`
  font-size: 1.5rem;
  display: flex;
  align-items: center;
`

const SearchSection1 = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const SearchSection2 = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid ${colors.inputBorderWhite};
`

const Favourite = styled.div`
  background-color: ${colors.white};
  width: 30%;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  min-height: 19em;
  height: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
  }
  
  @media (max-width: 1024px) {
    width: 100%;
  }
`

const FavouriteHeading = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${colors.text};
  font-size: 1.5rem;
  font-weight: 600;
  border-bottom: 2px solid ${colors.inputBorderWhite};
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
`

const StarIcon = styled.span`
  font-size: 1.5rem;
  display: flex;
  align-items: center;
`

const FavouriteSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${colors.inputWhite};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colors.inputBorderWhite};
    border-radius: 3px;
    
    &:hover {
      background: ${colors.textMed};
    }
  }
`

const FavouriteData = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem;
  background: ${colors.inputWhite};
  border-radius: 12px;
  border: 1px solid ${colors.inputBorderWhite};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${colors.white};
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
    transform: translateY(-2px);
  }
`

const FavouriteDataLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`

const FavouriteDataRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
  text-align: right;
`

const ClientName = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: ${colors.textBlue};
  margin: 0;
  word-break: break-word;
`

const LeadCountBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
  color: ${colors.white};
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  width: fit-content;
`

const LeadId = styled.p`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;
`

const DateText = styled.p`
  font-size: 0.75rem;
  color: ${colors.textMed};
  margin: 0;
`

const EmptyFavouriteState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
`

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`

const EmptyText = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: ${colors.text};
  margin: 0 0 0.5rem 0;
`

const EmptySubtext = styled.p`
  font-size: 0.875rem;
  color: ${colors.textMed};
  margin: 0;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 200px;
  gap: 1rem;
`

const LoadingText = styled.p`
  color: ${colors.textMed};
  font-size: 0.875rem;
  margin: 0;
`

const InputSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const SearchButton = styled.button`
  padding: 0.75rem 1.5rem;
  color: ${colors.white};
  background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(36, 147, 147, 0.2);
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(36, 147, 147, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ClearButton = styled.button`
  padding: 0.75rem 1.5rem;
  color: ${colors.text};
  background-color: ${colors.white};
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background-color: ${colors.inputWhite};
    border-color: ${colors.textMed};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const SearchIconSmall = styled.span`
  font-size: 1rem;
`

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid ${colors.white};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const ViewAllButton = styled.button`
  padding: 0.75rem 1.5rem;
  color: ${colors.brown};
  background-color: ${colors.white};
  border: 1px solid ${colors.brown};
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  margin-top: 1rem;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    background-color: ${colors.brown};
    color: ${colors.white};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(36, 147, 147, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
`

const StaffInputFeild = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;